import { doc, serverTimestamp } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedUpdateDoc as updateDoc } from './enforcedTracking';
import { getCollectionForCompany } from './dataAdapter';
import { EmployeeCashManager } from './employeeCashManager';
import { isEmployeeActive } from './employeeStatusUtils';

export function validateSalaryForm({
  salaryEmployeeId,
  salaryDate,
  salaryMonth,
  performanceSalaryRows,
  salaryDeductions,
}) {
  if (!salaryEmployeeId) return 'Select employee';
  if (!salaryDate) return 'Select date';
  if (!salaryMonth) return 'Select month';
  for (const row of performanceSalaryRows) {
    if (!row.type) return 'Select type for all performance components';
    if (!row.amount || isNaN(row.amount) || Number(row.amount) <= 0) {
      return 'Enter valid amount for all performance components';
    }
  }
  for (const deduction of salaryDeductions) {
    if (!deduction.reason) return 'Enter reason for all deductions';
    if (!deduction.amount || isNaN(deduction.amount) || Number(deduction.amount) <= 0) {
      return 'Enter valid amount for all deductions';
    }
  }
  return '';
}

export async function submitSalaryPayment({
  db,
  companyId,
  appId,
  userId,
  auth,
  employees,
  contextEmployees,
  irregularPayments,
  salaryEmployeeId,
  salaryDate,
  salaryMonth,
  fixedSalaryRows,
  performanceSalaryRows,
  salaryDeductions,
  appliedIrregularSelections,
  salaryPaymentMode,
  adjustSalaryFromBalance,
  salaryBalanceAmount,
  salaryBalanceSource,
  adjustSalaryFromOtherWallets,
  salaryOtherWalletsAmount,
  salarySourceEmployeeId,
  salaryBalanceSourceOther,
  reloadSalaryPayments,
  success,
  warning,
}) {
  const emp = employees.find((e) => e.employeeId === salaryEmployeeId);
  if (!emp || !isEmployeeActive(emp)) {
    return { ok: false, error: 'Selected employee is inactive. Salary can be processed only for active employees.' };
  }

  const fixedTotal = fixedSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const performanceTotal = performanceSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const pendingForEmp = irregularPayments.filter(
    (p) => !p.applied && p.personType === 'Employee' && p.employeeId === salaryEmployeeId
  );
  const irregularEarningsTotal = pendingForEmp.reduce(
    (sum, p) => sum + (appliedIrregularSelections[p.id] === 'Earning' ? Number(p.amount) || 0 : 0),
    0
  );
  const irregularDeductionsTotal = pendingForEmp.reduce(
    (sum, p) => sum + (appliedIrregularSelections[p.id] === 'Deduction' ? Number(p.amount) || 0 : 0),
    0
  );
  const totalEarnings = fixedTotal + performanceTotal + irregularEarningsTotal;
  const totalDeductions =
    salaryDeductions.reduce((sum, deduction) => sum + (Number(deduction.amount) || 0), 0) +
    irregularDeductionsTotal;
  const netAmount = totalEarnings - totalDeductions;
  const irregularApplied = pendingForEmp
    .filter((p) => appliedIrregularSelections[p.id])
    .map((p) => ({
      id: p.id,
      paymentType: p.paymentType,
      amount: Number(p.amount) || 0,
      appliedAs: appliedIrregularSelections[p.id],
      date: p.date,
    }));

  const totalFromWallets =
    (adjustSalaryFromBalance ? salaryBalanceAmount : 0) +
    (adjustSalaryFromOtherWallets ? salaryOtherWalletsAmount : 0);
  if (totalFromWallets > netAmount) {
    return {
      ok: false,
      error: `Total amount from wallets (₹${totalFromWallets.toLocaleString('en-IN')}) cannot exceed net salary (₹${netAmount.toLocaleString('en-IN')}).`,
    };
  }

  const companyCtx = { companyId, appId, userId };
  const salaryDocRef = await addDoc(getCollectionForCompany(db, companyCtx, 'salaryPayments'), {
    employeeId: salaryEmployeeId,
    employeeName: emp?.name || '',
    post: emp?.designation || '',
    date: salaryDate,
    month: salaryMonth,
    fixedRows: fixedSalaryRows,
    performanceRows: performanceSalaryRows,
    deductions: salaryDeductions,
    irregularApplied,
    irregularEarningsTotal,
    irregularDeductionsTotal,
    totalEarnings,
    totalDeductions,
    netAmount,
    paymentMode: salaryPaymentMode,
    createdAt: serverTimestamp(),
  });

  if (adjustSalaryFromBalance && salaryBalanceAmount > 0) {
    try {
      const companyData = { companyId, userId, appId };
      const employeeDoc = contextEmployees?.find((e) => e.employeeId === salaryEmployeeId);
      const employeeIdForCash = employeeDoc?.id || salaryEmployeeId;
      const adjustmentResult = await EmployeeCashManager.adjustSalaryFromBalance(companyData, {
        employeeId: employeeIdForCash,
        employeeName: emp?.name || '',
        amount: salaryBalanceAmount,
        balanceSource: salaryBalanceSource,
        salaryId: salaryDocRef.id,
        salaryMonth,
        description: `Salary payment for ${salaryMonth}`,
        date: salaryDate,
        createdBy: { userId, name: auth.currentUser?.displayName || 'User' },
      });
      if (adjustmentResult.success) {
        success(`Salary adjusted from employee cash balance: ₹${salaryBalanceAmount.toLocaleString('en-IN')}`);
      } else {
        warning(`Salary saved but balance adjustment failed: ${adjustmentResult.error}`);
      }
    } catch (balanceError) {
      console.error('Error adjusting salary from balance:', balanceError);
      warning('Salary saved but failed to adjust from cash balance: ' + balanceError.message);
    }
  }

  if (adjustSalaryFromOtherWallets && salaryOtherWalletsAmount > 0 && salarySourceEmployeeId) {
    try {
      const companyData = { companyId, userId, appId };
      const employeeDoc = contextEmployees?.find((e) => e.employeeId === salaryEmployeeId);
      const sourceEmp = contextEmployees?.find((e) => e.id === salarySourceEmployeeId);
      if (!sourceEmp) {
        warning('Salary saved but selected source employee not found.');
      } else {
        const otherResult = await EmployeeCashManager.adjustSalaryFromSpecifiedEmployeeWallet(companyData, {
          sourceEmployeeId: salarySourceEmployeeId,
          sourceEmployeeName: sourceEmp.name || '',
          sourceEmployee: sourceEmp,
          salaryEmployeeId: employeeDoc?.employeeId || salaryEmployeeId,
          salaryEmployeeName: emp?.name || '',
          amount: salaryOtherWalletsAmount,
          balanceSource: salaryBalanceSourceOther,
          salaryId: salaryDocRef.id,
          salaryMonth,
          description: `Salary payment for ${salaryMonth}`,
          date: salaryDate,
          createdBy: { userId, name: auth.currentUser?.displayName || 'User' },
        });
        if (otherResult.success) {
          await updateDoc(doc(getCollectionForCompany(db, companyCtx, 'salaryPayments'), salaryDocRef.id), {
            settledFromOtherWallet: {
              amount: salaryOtherWalletsAmount,
              sourceEmployeeId: sourceEmp.employeeId || sourceEmp.id,
              sourceEmployeeName: sourceEmp.name || '',
              sourceEmployeeDocId: salarySourceEmployeeId,
            },
          });
          success(
            `Salary settled from ${sourceEmp.name}'s wallet: ₹${salaryOtherWalletsAmount.toLocaleString('en-IN')}`
          );
        } else {
          warning(`Salary saved but settlement from wallet failed: ${otherResult.error}`);
        }
      }
    } catch (otherBalanceError) {
      console.error('Error settling salary from wallet:', otherBalanceError);
      warning('Salary saved but failed to settle from wallet: ' + otherBalanceError.message);
    }
  } else if (adjustSalaryFromOtherWallets && salaryOtherWalletsAmount > 0 && !salarySourceEmployeeId) {
    warning('Salary saved but please select a source employee whose wallet to use.');
  }

  try {
    for (const item of irregularApplied) {
      await updateDoc(doc(getCollectionForCompany(db, companyCtx, 'irregularPayments'), item.id), {
        applied: true,
        settled: true,
        appliedAt: serverTimestamp(),
        appliedInMonth: salaryMonth,
        appliedInPaymentDate: salaryDate,
        appliedAs: item.appliedAs,
        settlementRemark: `Settled in salary for ${salaryMonth}`,
        settledAt: serverTimestamp(),
      });
    }
  } catch (e) {
    console.warn('Failed to mark irregular payments applied', e);
  }

  await reloadSalaryPayments();
  return { ok: true };
}
